// Import the RTK Query methods from the React-specific entry point
import axios from '@/@axios';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import { ApiErrorObject, ApiErrorResponse, apiErrorResponseSchema } from './types';

// Lazy import to avoid circular dependency: store -> apiSlice -> store
const getAuthHelpers = async () => {
  const [storeModule, authModule] = await Promise.all([
    import('@/app/store'),
    import('app/store/authSlice'),
  ]);
  return {
    store: storeModule.default,
    isTokenExpired: authModule.isTokenExpired,
    refreshToken: authModule.refreshToken,
  };
};

const axiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
      headers?: AxiosRequestConfig['headers'];
      responseType?: AxiosRequestConfig['responseType'];
    },
    unknown,
    unknown
  > =>
  async ({ url, method, data, params, headers, responseType }) => {
    const baseUrl = import.meta.env.VITE_API_URL;

    if (method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH') {
      // channel.postMessage('httpRequest');
    }

    try {
      // Use lazy import to avoid circular dependency
      const { store, isTokenExpired, refreshToken } = await getAuthHelpers();
      const authState = store.getState().auth;

      if (isTokenExpired(authState)) {
        await store.dispatch(refreshToken());
      }

      const result = await axios({
        url: baseUrl + url,
        method,
        data,
        params,
        headers,
        responseType,
      });
      return { data: result.data, meta: result.headers['created-entity-id'] };
    } catch (axiosError) {
      const err = axiosError as AxiosError<ApiErrorResponse | Blob>;
      if (responseType === 'blob' && err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          const errorData = apiErrorResponseSchema.validateSync(parsed);

          return {
            error: {
              status: err.response.status,
              data: errorData.error.message,
            },
          };
        } catch (validationError) {
          console.error('Error parsing blob response:', validationError);

          return {
            error: {
              status: err.response?.status || 500,
              data: {
                code: 'parse_error',
                message: 'Failed to parse error response',
                internalMessage:
                  validationError instanceof Error
                    ? validationError.message
                    : 'Unknown parsing error',
                stackTraceMessage: null,
              } as ApiErrorObject,
            },
          };
        }
      }

      // Handle regular JSON errors
      const errorData = err.response?.data as ApiErrorResponse | undefined;

      return {
        error: {
          status: err.response?.status,
          data: errorData?.error?.message ?? err.message ?? 'An unknown error occurred',
        },
      };
    }
  };

// Define our single API slice object
export const apiSlice = createApi({
  // The cache reducer expects to be added at `state.api` (already default - this is optional)
  reducerPath: 'api',
  // All of our requests will have URLs starting with '/fakeApi'
  baseQuery: axiosBaseQuery(),
  // The "endpoints" represent operations and requests for this server
  tagTypes: [
    'Order',
    'Orders',
    'Client',
    'Clients',
    'Users',
    'User',
    'UserLogo',
    'Ticket',
    'Tickets',
    'Roles',
    'RolesRank',
    'Companies',
    'Company',
    'RateCards',
    'RateCard',
    'Providers',
    'Provider',
    'Licensees',
    'Activity',
    'Notes',
    'Services',
    'Service',
    'ProviderServices',
    'Invites',
    'Payors',
    'Payor',
    'Quotes',
    'Quote',
    'Subscriptions',
    'UnassignedSubscriptions',
    'ServiceOptions',
    'Deposits',
    'Deposit',
    'DepositItems',
    'DepositItem',
    'Goals',
    'Goal',
    'Vault',
    'SalesStatsReport',
    'CancellationReasons',
    'MessageTemplates',
    'MessagingRules',
    'ClientSubscriptions',
    'ActivityTypes',
    'UserActivities',
    'SalesRepUsers',
    'Testimonials',
    'CompanyImportMapping',
    'Payments',
    'Payment',
    'PayItems',
    'PayItem',
    'ResearchOptions',
    'MessagingRuleOptOuts',
    'ProviderServiceTypeOptions',
  ],
  endpoints: () => ({}),
});

// Export the auto-generated hook for the `getPosts` query endpoint
